// src/services/api.js
const API_BASE_URL = 'https://shop.neoteeforge.com/neotee-backend';

// Helper function to handle API calls
async function apiCall(endpoint, options = {}) {
  const token = localStorage.getItem('neoteeforge_auth_token');
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    },
  };

  const config = {
    ...defaultOptions,
    ...options,
  };

  // Ensure body is properly stringified for non-GET requests
  if (config.body && typeof config.body === 'object' && config.method !== 'GET') {
    config.body = JSON.stringify(config.body);
  }

  try {
    console.log(`🌐 API Call: ${endpoint}`, config.method || 'GET');
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    
    // Get the response text first
    const responseText = await response.text();
    
    let data;
    
    // Try to parse as JSON
    try {
      data = responseText ? JSON.parse(responseText) : {};
    } catch (parseError) {
      console.warn(`⚠️ Response is not JSON:`, responseText);
      throw new Error('Server returned invalid response');
    }

    // Check if the response indicates failure
    if (!response.ok) {
      const errorMessage = data.message || data.error || `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    console.error(`❌ API Error (${endpoint}):`, error);
    throw error;
  }
}

// Health check
export const healthCheck = async () => {
  try {
    const response = await apiCall('/api/health');
    return { 
      status: 'connected', 
      success: true,
      ...response 
    };
  } catch (error) {
    return { 
      status: 'disconnected', 
      success: false,
      message: 'Backend not available' 
    };
  }
};

// Auth API
export const authAPI = {
  login: async (credentials) => {
    try {
      if (!credentials.email || !credentials.password) {
        throw new Error('Email and password are required');
      }

      const data = await apiCall('/api/login', {
        method: 'POST',
        body: {
          email: credentials.email,
          password: credentials.password
        },
      });
      
      // Store token and user data
      if (data.token) {
        localStorage.setItem('neoteeforge_auth_token', data.token);
        localStorage.setItem('neoteeforge_auth_user', JSON.stringify(data.user));
      }
      
      return {
        success: true,
        token: data.token,
        user: data.user,
        message: data.message || 'Login successful'
      };
    } catch (error) {
      console.error('Login API error:', error);
      throw error;
    }
  },

  signup: async (userData) => {
    try {
      if (!userData.name || !userData.email || !userData.password) {
        throw new Error('Name, email and password are required');
      }

      const data = await apiCall('/api/signup', {
        method: 'POST',
        body: {
          name: userData.name,
          email: userData.email,
          password: userData.password
        },
      });
      
      // Store token and user data
      if (data.token) {
        localStorage.setItem('neoteeforge_auth_token', data.token);
        localStorage.setItem('neoteeforge_auth_user', JSON.stringify(data.user));
      }
      
      return {
        success: true,
        token: data.token,
        user: data.user,
        message: data.message || 'Registration successful'
      };
    } catch (error) {
      console.error('Signup API error:', error);
      throw error;
    }
  },

  logout: async () => {
    localStorage.removeItem('neoteeforge_auth_token');
    localStorage.removeItem('neoteeforge_auth_user');
    return { success: true, message: 'Logged out successfully' };
  },

  getStoredUser: () => {
    try {
      const userData = localStorage.getItem('neoteeforge_auth_user');
      const token = localStorage.getItem('neoteeforge_auth_token');
      
      if (userData && token) {
        return {
          user: JSON.parse(userData),
          token: token
        };
      }
      return null;
    } catch (error) {
      return null;
    }
  }
};

// User API
export const userAPI = {
  getProfile: async () => {
    try {
      const data = await apiCall('/api/user/profile');
      return {
        success: true,
        user: data.user,
        stats: data.stats,
        recentOrders: data.recentOrders,
        ...data
      };
    } catch (error) {
      console.error('Get profile API error:', error);
      throw error;
    }
  },

  getDashboard: async () => {
    try {
      const data = await apiCall('/api/user/dashboard');
      return {
        success: true,
        user: data.user,
        stats: data.stats,
        recentOrders: data.recentOrders,
        totalOrders: data.totalOrders,
        ...data
      };
    } catch (error) {
      console.error('Get user dashboard API error:', error);
      throw error;
    }
  }
};

// Products API
export const productsAPI = {
  getAll: async () => {
    try {
      const data = await apiCall('/api/products');
      return data.products || [];
    } catch (error) {
      console.error('Failed to fetch products:', error);
      return [];
    }
  },

  getFeatured: async () => {
    try {
      const data = await apiCall('/api/products/featured');
      return data.products || [];
    } catch (error) {
      console.error('Failed to fetch featured products:', error);
      return [];
    }
  },

  getById: async (id) => {
    try {
      const data = await apiCall(`/api/products/${id}`);
      return {
        success: true,
        product: data.product,
        ...data
      };
    } catch (error) {
      console.error(`Failed to fetch product ${id}:`, error);
      throw new Error('Unable to load product details.');
    }
  },

  search: async (query) => {
    try {
      if (!query || query.trim() === '') {
        return [];
      }

      const data = await apiCall(`/api/products/search?q=${encodeURIComponent(query.trim())}`);
      return data.products || [];
    } catch (error) {
      console.error('Search failed:', error);
      return [];
    }
  },

  getAllAdmin: async () => {
    try {
      const data = await apiCall('/api/admin/products');
      return data.products || [];
    } catch (error) {
      console.error('Failed to fetch admin products:', error);
      return [];
    }
  },

  create: async (productData) => {
    try {
      const data = await apiCall('/api/admin/products', {
        method: 'POST',
        body: productData,
      });
      return {
        success: true,
        product: data.product,
        message: data.message || 'Product created successfully'
      };
    } catch (error) {
      console.error('Create product error:', error);
      throw error;
    }
  },

  update: async (id, productData) => {
    try {
      const data = await apiCall(`/api/admin/products/${id}`, {
        method: 'PUT',
        body: productData,
      });
      return {
        success: true,
        product: data.product,
        message: data.message || 'Product updated successfully'
      };
    } catch (error) {
      console.error('Update product error:', error);
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const data = await apiCall(`/api/admin/products/${id}`, {
        method: 'DELETE',
      });
      return {
        success: true,
        message: data.message || 'Product deleted successfully'
      };
    } catch (error) {
      console.error('Delete product error:', error);
      throw error;
    }
  }
};

// Orders API
export const ordersAPI = {
  create: async (orderData) => {
    try {
      console.log('📦 Creating order:', orderData);
      
      const data = await apiCall('/api/orders', {
        method: 'POST',
        body: orderData,
      });
      
      console.log('✅ Order creation response:', data);
      
      return {
        success: data.success === true,
        order: data.order,
        orderId: data.orderId || data.order?.id,
        razorpayOrderId: data.razorpayOrderId || null,
        message: data.message || 'Order placed successfully'
      };
    } catch (error) {
      console.error('❌ Create order error:', error);
      throw new Error('Unable to place order. ' + error.message);
    }
  },

  getMyOrders: async () => {
    try {
      console.log('📋 Fetching user orders...');
      const data = await apiCall('/api/orders');
      
      return {
        success: data.success === true,
        orders: data.orders || [],
        count: data.count || 0,
        message: data.message
      };
    } catch (error) {
      console.error('❌ Failed to fetch user orders:', error);
      throw error;
    }
  },

  getById: async (orderId) => {
    try {
      const data = await apiCall(`/api/orders/${orderId}`);
      
      return {
        success: data.success === true,
        order: data.order,
        message: data.message
      };
    } catch (error) {
      console.error(`❌ Failed to fetch order ${orderId}:`, error);
      throw error;
    }
  },

  cancel: async (orderId) => {
    try {
      const data = await apiCall(`/api/orders/${orderId}/cancel`, {
        method: 'PUT',
      });
      
      return {
        success: data.success === true,
        message: data.message || 'Order cancelled successfully'
      };
    } catch (error) {
      console.error('❌ Cancel order error:', error);
      throw error;
    }
  },

  track: async (orderId) => {
    try {
      const data = await apiCall(`/api/orders/${orderId}/track`);
      return {
        success: data.success === true,
        tracking: data.tracking,
        ...data
      };
    } catch (error) {
      console.error('❌ Track order error:', error);
      throw error;
    }
  },

  verifyPayment: async (paymentData) => {
    try {
      console.log('🔐 Verifying payment:', paymentData);
      
      const data = await apiCall('/api/orders/verify-payment', {
        method: 'POST',
        body: paymentData
      });
      
      console.log('✅ Payment verification response:', data);
      
      return {
        success: data.success === true,
        verified: data.verified || false,
        order: data.order,
        message: data.message || 'Payment verification completed'
      };
    } catch (error) {
      console.error('❌ Verify payment error:', error);
      throw error;
    }
  },

  getAll: async () => {
    try {
      const data = await apiCall('/api/admin/orders');
      
      return {
        success: data.success === true,
        orders: data.orders || []
      };
    } catch (error) {
      console.error('❌ Failed to fetch all orders:', error);
      throw error;
    }
  },

  updateStatus: async (orderId, status) => {
    try {
      const data = await apiCall(`/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        body: { status },
      });
      
      return {
        success: data.success === true,
        message: data.message || 'Order status updated successfully'
      };
    } catch (error) {
      console.error('❌ Update order status error:', error);
      throw error;
    }
  },

  getOrderStats: async () => {
    try {
      const data = await apiCall('/api/admin/orders/stats');
      
      return {
        success: data.success === true,
        stats: data.stats
      };
    } catch (error) {
      console.error('❌ Get order stats error:', error);
      throw error;
    }
  }
};

// Dashboard API
export const dashboardAPI = {
  getAdminStats: async () => {
    try {
      console.log('📊 Fetching admin dashboard stats...');
      const data = await apiCall('/api/admin/dashboard');
      console.log('📊 Dashboard response:', data);
      
      if (data && data.success) {
        return {
          success: true,
          data: data.data || data,
          ...data
        };
      } else {
        // Return demo data
        return {
          success: true,
          data: getMockStats(),
          isDemo: true
        };
      }
    } catch (error) {
      console.error('❌ Failed to fetch admin dashboard:', error);
      // Return demo data on error
      return {
        success: true,
        data: getMockStats(),
        isDemo: true,
        error: error.message
      };
    }
  },

  getAdminProducts: async () => {
    try {
      const data = await apiCall('/api/admin/dashboard/products');
      return {
        success: true,
        data: data.data || data,
        ...data
      };
    } catch (error) {
      console.error('❌ Failed to fetch dashboard products:', error);
      // Fallback to regular products API
      const products = await productsAPI.getAll();
      return {
        success: true,
        data: products,
        isFallback: true
      };
    }
  },

  addProduct: async (productData) => {
    try {
      const data = await apiCall('/api/admin/dashboard/products', {
        method: 'POST',
        body: productData,
      });
      return {
        success: true,
        ...data
      };
    } catch (error) {
      console.error('❌ Failed to add product via dashboard:', error);
      // Fallback to regular products API
      return await productsAPI.create(productData);
    }
  },

  updateProduct: async (productId, productData) => {
    try {
      const data = await apiCall(`/api/admin/dashboard/products/${productId}`, {
        method: 'PUT',
        body: productData,
      });
      return {
        success: true,
        ...data
      };
    } catch (error) {
      console.error('❌ Failed to update product via dashboard:', error);
      // Fallback to regular products API
      return await productsAPI.update(productId, productData);
    }
  },

  deleteProduct: async (productId) => {
    try {
      const data = await apiCall(`/api/admin/dashboard/products/${productId}`, {
        method: 'DELETE',
      });
      return {
        success: true,
        ...data
      };
    } catch (error) {
      console.error('❌ Failed to delete product via dashboard:', error);
      // Fallback to regular products API
      return await productsAPI.delete(productId);
    }
  },

  getUserDashboard: async () => {
    try {
      const data = await apiCall('/api/user/dashboard');
      return {
        success: true,
        data: data.data || data,
        ...data
      };
    } catch (error) {
      console.error('❌ Failed to fetch user dashboard:', error);
      throw error;
    }
  }
};

// Admin Users API
export const adminAPI = {
  getUsers: async () => {
    try {
      const data = await apiCall('/api/admin/users');
      return {
        success: true,
        users: data.users || [],
        ...data
      };
    } catch (error) {
      console.error('❌ Failed to fetch users:', error);
      throw error;
    }
  }
};

// Book API
export const bookAPI = {
  getLibrary: async () => {
    try {
      const data = await apiCall('/api/books/library');
      return {
        success: true,
        library: data.library || [],
        count: data.count || 0,
        ...data
      };
    } catch (error) {
      console.error('❌ Failed to fetch library:', error);
      throw error;
    }
  },

  getBook: async (productId) => {
    try {
      const data = await apiCall(`/api/books/${productId}`);
      return {
        success: true,
        book: data.book,
        ...data
      };
    } catch (error) {
      console.error(`❌ Failed to fetch book ${productId}:`, error);
      throw error;
    }
  },

  downloadBook: async (productId) => {
    try {
      window.open(`${API_BASE_URL}/api/books/${productId}/download`, '_blank');
      return { success: true, message: 'Download started' };
    } catch (error) {
      console.error(`❌ Download failed:`, error);
      throw error;
    }
  },

  readOnline: async (productId) => {
    try {
      window.open(`${API_BASE_URL}/api/books/${productId}/read`, '_blank');
      return { success: true, message: 'Opening book...' };
    } catch (error) {
      console.error(`❌ Failed to open book:`, error);
      throw error;
    }
  },

  getPreview: async (productId) => {
    try {
      window.open(`${API_BASE_URL}/api/books/${productId}/preview`, '_blank');
      return { success: true, message: 'Opening preview...' };
    } catch (error) {
      console.error(`❌ Failed to open preview:`, error);
      throw error;
    }
  },

  uploadPdf: async (productId, formData) => {
    try {
      const token = localStorage.getItem('neoteeforge_auth_token');
      
      const response = await fetch(`${API_BASE_URL}/api/admin/books/${productId}/upload-pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'PDF upload failed');
      }

      return await response.json();
    } catch (error) {
      console.error('❌ PDF upload failed:', error);
      throw error;
    }
  },

  uploadSample: async (productId, formData) => {
    try {
      const token = localStorage.getItem('neoteeforge_auth_token');
      
      const response = await fetch(`${API_BASE_URL}/api/admin/books/${productId}/upload-sample`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Sample upload failed');
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Sample upload failed:', error);
      throw error;
    }
  }
};

// Helper function for demo data
const getMockStats = () => {
  return {
    overview: {
      totalUsers: 42,
      totalProducts: 15,
      totalOrders: 128,
      totalRevenue: 24500,
      pendingOrders: 8,
      completedOrders: 95
    },
    today: {
      orders: 5,
      revenue: 1200,
      newUsers: 3
    },
    recentOrders: [
      { id: 1001, customer_name: 'John Doe', total: 1200, status: 'delivered', created_at: new Date().toISOString() },
      { id: 1002, customer_name: 'Jane Smith', total: 800, status: 'pending', created_at: new Date().toISOString() },
      { id: 1003, customer_name: 'Bob Johnson', total: 1500, status: 'shipped', created_at: new Date().toISOString() }
    ],
    popularProducts: []
  };
};

// Export everything
export default {
  healthCheck,
  authAPI,
  userAPI,
  productsAPI,
  ordersAPI,
  dashboardAPI,
  adminAPI,
  bookAPI
};